import type { admin } from '@repletoryreach/sdk-api';
import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';

export type Queue = admin.QueueStat;

/**
 * The ready-message depth at which this console calls a queue backlogged.
 * One constant, used by the KPI count, the filter, the badge and the copy, so
 * the number in the chip label can never drift from the number that decides
 * which rows the chip counts.
 */
export const BACKLOG_WARN = 100;

/**
 * RabbitMQ scopes a queue name to a vhost, so the name alone is not an
 * identity. Using it as one produced duplicate React keys and quietly merged
 * two distinct queues on a multi-vhost broker.
 */
export function queueKey(q: Queue): string {
  return `${q.vhost}/${q.name}`;
}

/** A queue holding messages with nobody reading them is what "stuck" means. */
export function hasNoConsumers(q: Queue): boolean {
  return (q.consumers ?? 0) === 0 && (q.messages ?? 0) > 0;
}

export function isBacklogged(q: Queue): boolean {
  return (q.messages_ready ?? 0) > BACKLOG_WARN;
}

/**
 * One state vocabulary for all three views, expressed as `StatusBadge` tones
 * so both themes are handled by the design system rather than by an inline
 * colour per view.
 */
export function queueTone(q: Queue): StatusTone {
  if (hasNoConsumers(q)) return 'down';
  if ((q.messages_unacked ?? 0) > 0 || isBacklogged(q)) return 'warning';
  return q.state === 'running' ? 'healthy' : 'inactive';
}

/**
 * What this page has watched for one queue: a point per poll. `at` is the
 * poll's own timestamp, so a rate can be worked out from the counters without
 * assuming the refresh interval was honoured.
 */
export interface QueueSamples {
  ready: number[];
  unacked: number[];
  published: number[];
  delivered: number[];
  at: number[];
}

/** Messages a second in and out, and where the figure came from. */
export interface QueueRate {
  publish: number;
  deliver: number;
  /** True when this page measured it across its own polls, false when it is the broker's average. */
  measured: boolean;
}

/**
 * The broker publishes both a rate and the counter behind it, and the rate is
 * the less useful half: it is an average over RabbitMQ's own sample window, so
 * it reads 0 between windows and stays 0 on a broker whose metrics collector
 * is not sampling. The counters only ever climb, so two polls and their
 * timestamps give a rate that is true for the period this page watched.
 *
 * The broker's own figure is the fallback for the first poll, when there is
 * nothing to difference yet. A counter that went backwards means the broker
 * restarted and the series is meaningless, so it is discarded rather than
 * reported as a negative rate.
 */
export function queueRate(q: Queue, samples?: QueueSamples): QueueRate {
  const n = samples?.at.length ?? 0;
  if (samples && n > 1) {
    const seconds = (samples.at[n - 1] - samples.at[0]) / 1000;
    const published = samples.published[n - 1] - samples.published[0];
    const delivered = samples.delivered[n - 1] - samples.delivered[0];
    if (seconds >= 1 && published >= 0 && delivered >= 0) {
      return { publish: published / seconds, deliver: delivered / seconds, measured: true };
    }
  }
  return { publish: q.publish_rate ?? 0, deliver: q.deliver_rate ?? 0, measured: false };
}

/**
 * The suffixes MassTransit gives a queue it has given up on. `_error` holds
 * messages whose consumer threw; `_skipped` holds messages no consumer was
 * bound to. Both are business that did not happen, which is why the requeue
 * action is offered on the row rather than hidden in a menu for these.
 *
 * The action itself is allowed on any queue: this only decides prominence.
 */
export function isDeadLetterQueue(q: Queue): boolean {
  return q.name.endsWith('_error') || q.name.endsWith('_skipped');
}
